import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { cn, decodeHtmlEntities } from "@/lib/utils"


import { Button } from "@/components/ui/button"
import { Loader } from "lucide-react";

declare global {
    const wvcClient: any;
}

export interface AddToCartButtonProps extends React.HTMLAttributes<HTMLDivElement> {
    productId: number | string;
    // Content
    text?: React.ReactNode;
    loadingText?: React.ReactNode;
    icon?: React.ReactNode;
    // Configuration
    showQuantity?: boolean;
    minQuantity?: number;
    maxQuantity?: number;
    // Styling
    buttonClassName?: string;
    quantityClassName?: string;
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
    size?: "default" | "sm" | "lg" | "icon";
}

/** -------- Types -------- */
export interface EcommerceProduct {
    id: string;
    title: string;
    slug?: string;
    short_description?: string;
    long_description?: string;
    link?: string;
    date?: string;
    author?: Author;
    featured_image_url?: string;
    categories?: Term[];
    tags?: Term[];
    customFields?: Record<string, any>; // Custom post type fields from wvc_cpt_data meta
    price?: number;
    discounted_price?: number; // Falls back to price if not provided
}

export interface Term {
    id: number;
    name: string;
    slug: string;
    taxonomy: string; // e.g., "product_cat" or "product_tag"
    link?: string;
}

export interface Author {
    id: number;
    name: string;
    slug?: string;
    link?: string;
    avatar?: Record<string, string>; // Avatar image URLs keyed by size (e.g., "24", "48", "96")
}

type QueryParams = Record<string, any>;

interface EcommerceProductsContextType {
    // data
    posts: EcommerceProduct[];
    total: number;
    totalPages: number;

    // status
    loading: boolean;
    isRefetching: boolean;
    error: string | null;

    // current query state (editable from children)
    query: QueryParams;
    embeds: string[];
    fields: string[];

    /**
     * Mutation helpers (children can drive queries)
     *
     * - setQuery: merges into the existing query by default (update mode). Pass a function to fully replace.
     * - setEmbeds: replaces the entire array by default (reset mode). Pass a function to update/merge manually.
     * - setFields: same as setEmbeds — replace by default, update if you pass a function.
     */
    setQuery: (next: Partial<QueryParams> | ((prev: QueryParams) => QueryParams)) => void;
    setEmbeds: (next: string[] | ((prev: string[]) => string[])) => void;
    setFields: (next: string[] | ((prev: string[]) => string[])) => void;

    // pagination helpers
    hasNext: boolean;
    hasPrev: boolean;
    setPage: (page: number) => void;
    nextPage: () => void;
    prevPage: () => void;

    // network
    refetch: () => Promise<void>;
    fetch: (opts?: {
        wp_query?: Partial<QueryParams>;
        embeds?: string[];
        fields?: string[];
        append?: boolean;
        refresh?: boolean; // force isRefresh mode
    }) => Promise<void>;
}

// Convert a single WP REST post (with optional _embedded) into our Post type
const normalizeEcommerceProduct = (raw: any): EcommerceProduct => {
    const safeExtractText = (value: any): string | undefined => {
        if (!value) return undefined;
        if (typeof value === "string") return value;
        if (typeof value === "object" && typeof value.rendered === "string") {
            return value.rendered;
        }
        return undefined;
    };

    const title =
        decodeHtmlEntities(
            safeExtractText(raw?.title) ??
            raw?.post_title ??
            String(raw?.title || raw?.name || "Untitled")
        );

    const shortDescription =
        safeExtractText(raw?.short_description) ??
        safeExtractText(raw?.excerpt) ??
        raw?.post_excerpt ??
        undefined;

    const longDescription =
        safeExtractText(raw?.long_description) ??
        safeExtractText(raw?.content) ??
        raw?.post_content ??
        shortDescription ??
        undefined;

    const link = raw?.resolved_url || raw?.link || raw?.url || undefined;

    const featured_image_url =
        raw?._embedded?.["wp:featuredmedia"]?.[0]?.source_url ??
        raw?.featured_media_url ??
        raw?.featured_image_url ??
        undefined;

    // Helper to map a WP term object into our Term type
    const mapTerm = (t: any): Term => ({
        id: Number(t?.id ?? 0),
        name: String(t?.name ?? ""),
        slug: String(t?.slug ?? ""),
        taxonomy: String(t?.taxonomy ?? ""),
        link: t?.link,
    });

    // Category & tag objects from _embedded.wp:term (array of arrays)
    const termGroups: any[][] | undefined = raw?._embedded?.["wp:term"];
    const categories: Term[] | undefined = Array.isArray(termGroups)
        ? termGroups
            .reduce((acc, group) => acc.concat(group), [])
            .filter((t: any) => t && t.taxonomy === "product_cat")
            .map(mapTerm)
        : [];
    const tags: Term[] | undefined = Array.isArray(termGroups)
        ? termGroups
            .reduce((acc, group) => acc.concat(group), [])
            .filter((t: any) => t && t.taxonomy === "product_tag")
            .map(mapTerm)
        : [];

    // Extract custom fields from individual meta keys (for custom post types)
    // Meta keys follow pattern: wvc_{post_type}_{field_key}
    let customFields: Record<string, any> | undefined = undefined;
    if (raw?.meta) {
        const postType = raw?.type || 'product';
        const prefix = `wvc_${postType}_`;
        const fields: Record<string, any> = {};

        Object.keys(raw.meta).forEach(key => {
            if (key.startsWith(prefix)) {
                const fieldKey = key.replace(prefix, '');
                fields[fieldKey] = raw.meta[key];
            }
        });

        if (Object.keys(fields).length > 0) {
            customFields = fields;
        }
    }

    // Extract price fields (can be numbers or null)
    const price = raw?.price != null ? Number(raw.price) : 0;
    const discounted_price = raw?.discounted_price != null ? Number(raw.discounted_price) : price;

    return {
        id: String(raw?.id ?? raw?.ID),
        title: String(title),
        slug: raw?.slug,
        short_description: typeof shortDescription === "string" ? shortDescription : undefined,
        long_description: typeof longDescription === "string" ? longDescription : undefined,
        link,
        date: raw?.date || raw?.post_date || undefined,
        featured_image_url,
        categories: categories && categories.length ? categories : [],
        tags: tags && tags.length ? tags : [],
        customFields: customFields,
        price: price,
        discounted_price: discounted_price,
    };
};

export const convertWPResponseToEcommerceProducts = (input: any): EcommerceProduct[] => {
    if (!Array.isArray(input)) return [];
    return input.map(normalizeEcommerceProduct);
};

const EcommerceProductsContext = createContext<EcommerceProductsContextType | null>(null);

// hack to return the products as a property, instead of posts property for consumers
export const useEcommerceProducts = (): EcommerceProductsContextType & { products: EcommerceProduct[] } => {
    const ctx = useContext(EcommerceProductsContext);
    if (!ctx) throw new Error("useEcommerceProducts must be used within a WordPressEcommerceProductsProvider");
    return {
        ...ctx,
        products: ctx.posts,
    };
};

type WordPressEcommerceProductsProviderProps = {
    children: React.ReactNode;
    postType?: string;      // post type to fetch (defaults to 'product')
    wp_query?: QueryParams; // initial query args
    embeds?: string[];      // initial embeds
    fields?: string[];      // initial fields
} & React.HTMLAttributes<HTMLDivElement>;

// Helper function to ensure 'meta' is included for custom post types
const ensureMetaForCPT = (fields: string[], postType: string): string[] => {
    if (postType !== 'product' && !fields.includes('meta')) {
        return [...fields, 'meta'];
    }
    return fields;
};

const WordPressEcommerceProductsProvider = ({
    children,
    postType = 'product', // default to regular products
    wp_query = {},
    embeds = [],
    fields = [],
    ...divProps
}: WordPressEcommerceProductsProviderProps) => {
    // Editable query state (seeded from props)
    const [query, setQueryState] = useState<QueryParams>(wp_query);
    const [embedsState, setEmbedsState] = useState<string[]>(embeds);
    const [fieldsState, setFieldsState] = useState<string[]>(ensureMetaForCPT(fields, postType));

    // Data + status
    const [posts, setPosts] = useState<EcommerceProduct[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isRefetching, setIsRefetching] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [total, setTotal] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    // Sync state if parent props change (treat props as "initial but updatable")
    useEffect(() => {
        setQueryState(wp_query || {});
    }, [JSON.stringify(wp_query)]);

    useEffect(() => {
        setEmbedsState(embeds || []);
    }, [JSON.stringify(embeds)]);

    useEffect(() => {
        setFieldsState(ensureMetaForCPT(fields || [], postType));
    }, [JSON.stringify(fields), postType]);

    const handleError = useCallback((e?: unknown) => {
        const msg = (e as any)?.message ?? String(e);
        setError(msg);
    }, []);

    const effectiveDeps = [
        JSON.stringify(query),
        JSON.stringify(embedsState),
        JSON.stringify(fieldsState),
    ];

    const fetch = (opts?: {
        wp_query?: Partial<QueryParams>;
        embeds?: string[];
        fields?: string[];
        append?: boolean;
        refresh?: boolean;
    }): Promise<void> => {
        const isRefresh = Boolean(opts?.refresh);
        if (isRefresh && isRefetching) return Promise.resolve();

        setError(null);
        if (isRefresh) setIsRefetching(true);
        else setLoading(true);

        const mergedQuery: QueryParams = {
            ...query,
            ...(opts?.wp_query || {}),
        };
        const usedEmbeds = opts?.embeds ?? embedsState;
        const usedFields = opts?.fields ?? fieldsState;

        return wvcClient.getPosts({
            wp_query: mergedQuery,
            embeds: usedEmbeds,
            fields: usedFields,
        },
            postType = "product"
        ).then((res: any) => {
            const normalized = (res?.posts ?? []).map(normalizeEcommerceProduct);
            setPosts(prev => (opts?.append ? [...prev, ...normalized] : normalized));
            setTotal(Number(res?.total ?? 0));
            setTotalPages(Number(res?.total_pages ?? 0));
        }).catch((e: any) => {
            handleError(e);
        }).finally(() => {
            if (isRefresh) setIsRefetching(false);
            else setLoading(false);
        });
    };

    // Initial + reactive fetch when query knobs change
    useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, effectiveDeps);

    const refetch = (): Promise<void> => fetch({ refresh: true });

    // Expose convenient setters to children
    const setQuery: EcommerceProductsContextType["setQuery"] = (next) => {
        setQueryState(prev => (typeof next === "function" ? (next as any)(prev) : { ...prev, ...next }));
    };

    const setEmbeds: EcommerceProductsContextType["setEmbeds"] = (next) => {
        setEmbedsState(prev => (typeof next === "function" ? (next as any)(prev) : next));
    };

    const setFields: EcommerceProductsContextType["setFields"] = (next) => {
        setFieldsState(prev => (typeof next === "function" ? (next as any)(prev) : next));
    };

    // Pagination helpers derived from current query & totals
    const currentPage = Number(query?.page ?? 1) || 1;
    const hasNext = currentPage < (totalPages || 0);
    const hasPrev = currentPage > 1;

    const setPage = (page: number) => setQuery({ page: Math.max(1, Math.floor(page || 1)) });
    const nextPage = () => hasNext && setPage(currentPage + 1);
    const prevPage = () => hasPrev && setPage(currentPage - 1);

    const value: EcommerceProductsContextType = useMemo(() => ({
        // data
        posts,
        total,
        totalPages,

        // status
        loading,
        isRefetching,
        error,

        // state
        query,
        embeds: embedsState,
        fields: fieldsState,

        // setters
        setQuery,
        setEmbeds,
        setFields,

        // pagination
        hasNext,
        hasPrev,
        setPage,
        nextPage,
        prevPage,

        // network
        refetch,
        fetch,
    }), [
        posts,
        total,
        totalPages,
        loading,
        isRefetching,
        error,
        query,
        embedsState,
        fieldsState,
        hasNext,
        hasPrev,
    ]);

    return (
        <EcommerceProductsContext.Provider value={value}>
            <div
                {...divProps}
                data-wvc-dynamic="EcommerceProductsProvider"
                data-wvc-wp_query={JSON.stringify(wp_query)}
                data-wvc-embeds={JSON.stringify(embeds)}
                data-wvc-fields={JSON.stringify(fields)}
            >
                {children}
            </div>
        </EcommerceProductsContext.Provider>
    );
};

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
    productId,
    className,
    text = "Add to Cart",
    loadingText = "Adding...",
    icon,
    showQuantity = true,
    minQuantity = 1,
    maxQuantity = 99,
    buttonClassName,
    quantityClassName,
    variant = "default",
    size = "default",
    ...props
}) => {
    const [quantity, setQuantity] = useState(minQuantity);
    const [isAdding, setIsAdding] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const decrement = () => setQuantity(Math.max(minQuantity, quantity - 1));
    const increment = () => setQuantity(Math.min(maxQuantity, quantity + 1));

    const handleAddToCart = async () => {
        setIsAdding(true);
        setError(null);
        setSuccessMessage(null);

        try {
            if (wvcClient?.cart?.addToCart) {
                const item = {
                    id: productId,
                    quantity: quantity,
                    addedAt: new Date().toISOString()
                };

                const response = await wvcClient.cart.addToCart(item);

                if (response?.success) {
                    document.dispatchEvent(new Event("wvc_cart_updated"));
                    setSuccessMessage(response.message || "Product added to cart!");
                    setTimeout(() => setSuccessMessage(null), 3000);
                } else {
                    setError(response?.message || "Failed to add product to cart");
                }
            } else {
                console.error("wvcClient.cart.addToCart is not available");
                setError("Cart functionality unavailable");
            }
        } catch (err: any) {
            console.error("Add to cart error:", err);
            setError(err.message || "Failed to add product.");
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className={cn("flex flex-col gap-2", className)} {...props}>
            <div className="flex items-center gap-2">
                {showQuantity && (
                    <div className={cn("flex items-center border rounded-md", quantityClassName)}>
                        <button
                            className="px-3 py-1 hover:bg-gray-100 disabled:opacity-50"
                            onClick={decrement}
                            disabled={isAdding || quantity <= minQuantity}
                            type="button"
                        >
                            -
                        </button>
                        <span className="px-2 py-1 min-w-[2rem] text-center">{quantity}</span>
                        <button
                            className="px-3 py-1 hover:bg-gray-100 disabled:opacity-50"
                            onClick={increment}
                            disabled={isAdding || quantity >= maxQuantity}
                            type="button"
                        >
                            +
                        </button>
                    </div>
                )}

                <Button
                    onClick={handleAddToCart}
                    disabled={isAdding}
                    className={cn("flex-1 gap-2", buttonClassName)}
                    variant={variant}
                    size={size}
                >
                    {isAdding ? (
                        <>
                            <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
                            {loadingText}
                        </>
                    ) : (
                        <>
                            {icon}
                            {text}
                        </>
                    )}
                </Button>
            </div>

            {successMessage && (
                <p className="text-sm text-green-600 animate-in fade-in slide-in-from-top-1">
                    {successMessage}
                </p>
            )}

            {error && (
                <p className="text-sm text-red-600 animate-in fade-in slide-in-from-top-1">
                    {error}
                </p>
            )}
        </div>
    );
};


export { WordPressEcommerceProductsProvider, WordPressEcommerceProductsProvider as EcommerceProductsProvider, AddToCartButton };
